import {
  FlagIcon,
  ChatBubbleLeftEllipsisIcon,
  ShieldExclamationIcon,
  PhoneIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import { setActiveTab } from 'common/slice/activeTabSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { TABS } from './constant';
import { setActiveAttendee } from 'common/slice/activeAttendeeSlice';
import { addBreadcrumb } from 'common/slice/breadcrumbsSlice';
import { Tooltip } from 'react-tooltip';
import { getAudioCallStatus } from 'helpers/messages';
import { openModal } from 'common/slice/modalSlice';
import { SessionStatus } from 'globals/enums';
import { isSessionEnded } from 'helpers/session';
import { Attendee } from 'common/api/proctorType';
import * as Popover from '@radix-ui/react-popover';
import Button from './Button';
import { memo } from 'react';

interface Props {
  attendee: Attendee;
  isApprovalPending: boolean;
  isChatUnread: boolean;
  tooltipId: string;
  sessionId: string;
  sessionUuid: string;
  sessionStatus: string;
  isCriticalAlertUnread: boolean;
  sessionCompletedAt: string;
  sessionJoinedAt: string;
  isPaused?: boolean;
  isRightPanel?: boolean;
  secondaryCamera?: boolean;
  isSecondaryDisabled?: boolean;
  isPauseAllowed?: boolean;
}

const AttendeePaneComponent = ({
  sessionJoinedAt,
  sessionCompletedAt,
  attendee,
  isApprovalPending,
  isChatUnread,
  tooltipId,
  sessionId,
  sessionUuid,
  sessionStatus,
  isCriticalAlertUnread,
  isPaused,
  isRightPanel = false,
  secondaryCamera = false,
  isSecondaryDisabled = false,
  isPauseAllowed,
}: Props) => {
  const dispatch = useAppDispatch();
  const audioCall = useAppSelector((state) => state.audioCall);
  const { isDetailView } = useAppSelector((state) => state.activeAttendee);
  const activeTab = useAppSelector((state) => state.activeTab.tab);
  const tooltip = tooltipId ? tooltipId : `tile_tooltip_${attendee?.attendee_id}`;
  const actionTooltipId = tooltipId
    ? `action_${tooltipId}`
    : `tile_tooltip_action_${attendee?.attendee_id}`;
  const audioCallStatus = getAudioCallStatus(sessionId, audioCall.audioCallStatuses);

  const handleChange = (value: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(setActiveTab(value));
    dispatch(setActiveAttendee({ ...attendee, isDetailView }));
    const payload = {
      label: attendee?.attendee_external_id,
      url: '',
    };
    if (isDetailView) {
      dispatch(addBreadcrumb(payload));
    }
  };

  const handleAction = (type: string) => {
    dispatch(
      openModal({
        attendeeId: attendee?.attendee_id,
        type,
        sessionUuid,
        sessionId,
        slotEndDate: attendee?.slot_ends_at,
        slotStartDate: attendee?.slot_starts_at,
        sessionJoinedAt,
        sessionCompletedAt,
        bookingExternalId: attendee?.session?.booking_external_id || '',
      }),
    );
  };

  return (
    <div className="w-full pl-2 flex items-center justify-between gap-1 whitespace-nowrap overflow-hidden">
      <div
        className="min-w-24 text-sm cursor-pointer overflow-hidden text-ellipsis"
        onClick={(event) => handleChange(activeTab, event)}
        data-tooltip-id={tooltip}
      >
        {attendee?.attendee_external_id}
      </div>
      <Tooltip className="z-[1000]" id={tooltip} place="bottom">
        <div className="flex flex-col gap-1 text-xs">
          <div>
            Name: {attendee?.attendee_first_name ? attendee?.attendee_first_name : '--'}{' '}
            {attendee?.attendee_last_name ? attendee?.attendee_last_name : '--'}
          </div>
          <div>Email: {attendee?.attendee_email ? attendee?.attendee_email : '--'}</div>
          <div>
            Attendee ID: {attendee?.attendee_external_id ? attendee?.attendee_external_id : '--'}
          </div>
        </div>
      </Tooltip>
      {isPaused && !isRightPanel && (
        <div className="text-white bg-amber-300 text-xs py-1/2 px-2">Paused</div>
      )}
      <FlagIcon
        className={`h-4 w-4 cursor-pointer ${isCriticalAlertUnread ? 'text-red-500' : ''}`}
        onClick={(event) => handleChange(TABS.ALERT_LOGS, event)}
      />
      <ChatBubbleLeftEllipsisIcon
        className={`h-4 w-4 cursor-pointer ${isChatUnread ? 'text-red-500' : ''}`}
        onClick={(event) => handleChange(TABS.CHAT, event)}
      />
      <ShieldExclamationIcon
        className={`h-4 w-4 cursor-pointer ${isApprovalPending ? 'text-red-500' : ''}`}
        onClick={(event) => handleChange(TABS.APPROVALS, event)}
      />
      <PhoneIcon
        className={`h-4 w-4 cursor-pointer ${
          audioCallStatus === 'requested'
            ? 'text-yellow-500 fill-current'
            : audioCallStatus === 'connected'
            ? 'text-green-500 fill-current'
            : ''
        }`}
        onClick={(event) => handleChange(TABS.CHAT, event)}
      />
      <Popover.Root>
        <Popover.Trigger onClick={(event) => event.stopPropagation()}>
          <EllipsisVerticalIcon
            data-tooltip-id={actionTooltipId}
            className={`block h-4 w-4 cursor-pointer ${isChatUnread ? 'text-red-500' : ''}`}
          />
        </Popover.Trigger>
        {sessionStatus !== SessionStatus.NoShow && (
          <Popover.Portal>
            <Popover.Content
              side="top"
              align="end"
              collisionBoundary={document.body}
              collisionPadding={6}
              sideOffset={5}
            >
              <div className="gap-5 text-sm text-black bg-white popover-content bottom-5 -right-3">
                {!sessionId && (
                  <Button
                    label="Mark as No-Show"
                    onClick={() => handleAction(SessionStatus.NoShow)}
                    className="block p-2 cursor-pointer hover:text-blue-500 rounded-xl"
                  />
                )}
                {sessionId && !isSessionEnded(sessionStatus) && (
                  <Button
                    label="Terminate"
                    onClick={() => handleAction(SessionStatus.Terminated)}
                    className="block p-2 cursor-pointer hover:text-blue-500 rounded-xl"
                  />
                )}
                {sessionId && (
                  <Button
                    label="Add Flag"
                    onClick={() => handleAction('add-flag')}
                    className="block p-2 cursor-pointer hover:text-blue-500 rounded-xl"
                  />
                )}
                {sessionId && sessionStatus === SessionStatus.Online && !isPaused && (
                  <Button
                    label="Pause"
                    onClick={() => handleAction('pause')}
                    className="block p-2 cursor-pointer hover:enabled:text-blue-500 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={!isPauseAllowed}
                  />
                )}
                {sessionId && sessionStatus === SessionStatus.Online && isPaused && (
                  <Button
                    label="Resume"
                    onClick={() => handleAction('resume')}
                    className="block p-2 cursor-pointer hover:text-blue-500 rounded-xl"
                  />
                )}
                {sessionId && !isSessionEnded(sessionStatus) && (
                  <Button
                    label="Extend"
                    onClick={() => handleAction('extend')}
                    className="block p-2 cursor-pointer hover:text-blue-500 rounded-xl"
                  />
                )}
                {secondaryCamera && !isSessionEnded(sessionStatus) && (
                  <Button
                    label={
                      isSecondaryDisabled === true
                        ? 'Enable Secondary Camera'
                        : 'Skip Secondary Camera'
                    }
                    className="block p-2 cursor-pointer hover:text-blue-500 rounded-xl"
                    onClick={() => {
                      isSecondaryDisabled === true
                        ? handleAction('enable-secondary-camera')
                        : handleAction('disable-secondary-camera');
                    }}
                    type="button"
                  />
                )}
              </div>
            </Popover.Content>
          </Popover.Portal>
        )}
      </Popover.Root>
    </div>
  );
};

const areEqual = (prevProps: Props, nextProps: Props) =>
  prevProps.attendee?.attendee_id === nextProps.attendee?.attendee_id &&
  prevProps.sessionStatus === nextProps.sessionStatus &&
  prevProps.isChatUnread === nextProps.isChatUnread &&
  prevProps.isApprovalPending === nextProps.isApprovalPending &&
  prevProps.isCriticalAlertUnread === nextProps.isCriticalAlertUnread &&
  prevProps.isPaused === nextProps.isPaused &&
  prevProps.secondaryCamera === nextProps.secondaryCamera &&
  prevProps.isSecondaryDisabled === nextProps.isSecondaryDisabled &&
  prevProps.isRightPanel === nextProps.isRightPanel &&
  prevProps.tooltipId === nextProps.tooltipId &&
  prevProps.isPauseAllowed === nextProps.isPauseAllowed &&
  prevProps.sessionCompletedAt === nextProps.sessionCompletedAt &&
  prevProps.sessionJoinedAt === nextProps.sessionJoinedAt;

const AttendeePane = memo(AttendeePaneComponent, areEqual);

export default AttendeePane;
